import { BaseModel, ExecuteContext } from './framework';
import { Disposable } from '@cotera/utilities';
import { ModelConstructor, TargetableModel } from './framework/types';

/**
 * Disposable for a model that adds it to the context and removes it when disposed.
 * Useful for actions that need to modify the model.
 */
export class DisposableModelContext<T extends BaseModel> implements Disposable {
  constructor(
    private readonly context: ExecuteContext,
    private readonly model: T
  ) {
    this.context.addModels([this.model]);
  }

  async dispose() {
    this.context.removeModel(
      this.model.constructor as ModelConstructor,
      this.model.id
    );
  }
}

export type TargetableModelConstructor<
  T extends TargetableModel = TargetableModel
> = {
  new (...args: any[]): T;
};

export class DisposableTargetedModelContext<T extends TargetableModel>
  implements Disposable
{
  private current: T | null = null;
  constructor(
    private readonly context: ExecuteContext,
    private readonly model: T
  ) {
    this.current = this.context.targetedModelOfType(
      this.model.constructor as TargetableModelConstructor
    ) as T | null;
    if (this.current) {
      this.current.markAsNotTargeted();
    }
    this.context.addModels([this.model]);
    this.model.target();
  }

  async dispose() {
    this.context.removeModel(
      this.model.constructor as ModelConstructor,
      this.model.id
    );
    if (this.current) {
      this.context.targetModel(this.current);
    }
  }
}
